/**
 * Format Utilities
 * Currency, crypto, and number formatting functions
 */

import _ from 'lodash';
import formatCurrency from 'format-currency';

/**
 * Readable Crypto Format (0.00000009)
 * @param {number} value
 * @returns {string}
 */
export function toCrypto(value) {
  const number = value * 100000000;
  return number.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

/**
 * Readable Satoshi Format (0.00000010)
 * @param {number|string} val
 * @param {string} coin
 * @param {boolean} usdFormat
 * @param {boolean} prefix
 * @returns {string}
 */
export function forceSatoshiFormat(val, coin = 'BTC', usdFormat = false, prefix = true) {
  Number.prototype.format = function (n, x, s, c) {
    const re = '\\d(?=(\\d{' + (x || 3) + '})+' + (n > 0 ? '\\D' : '$') + ')';
    const num = this.toFixed(Math.max(0, ~~n));
    return (c ? num.replace('.', c) : num).replace(
      new RegExp(re, 'g'),
      '$&' + (s || ',')
    );
  };

  const amount = parseFloat(val);
  let changed;

  if (isNaN(amount)) {
    changed = val;
  } else {
    changed = Number(amount.format(8, 30, '.', '.')).toFixed(8);
  }

  if (usdFormat) {
    return cryptoToUsd(changed, coin, prefix);
  }
  return changed;
}

/**
 * Crypto to USD
 * @param {number|string} val
 * @param {string} coin
 * @param {boolean} prefix
 * @param {Object} gameRates - Game object with exchange rates (from socket, optional)
 * @returns {string}
 */
export function cryptoToUsd(val, coin = 'BTC', prefix, gameRates) {
  // Use provided rates or fallback to global Game
  const Game = gameRates || (typeof window !== 'undefined' ? window.Game : {});
  const usd = Game?.[_.upperCase(coin)] || 0; // By /Socket
  const convert = parseFloat(val) * parseFloat(usd);
  return toUSD(convert, prefix);
}

/**
 * USD to Crypto
 * @param {number} value
 * @param {string} coin
 * @param {Object} gameRates - Game object with exchange rates (from socket, optional)
 * @returns {string}
 */
export function usdToCrypto(value, coin, gameRates) {
  // Use provided rates or fallback to global Game
  const Game = gameRates || (typeof window !== 'undefined' ? window.Game : {});
  const usd = Game?.[_.upperCase(coin)] || 0; // By /Socket
  const convert = value / parseFloat(usd);
  return toUSD(_.toNumber(convert));
}

/**
 * USD Format
 * @param {number} value
 * @param {boolean} prefix
 * @returns {string}
 */
export function toUSD(value, prefix = true) {
  const opts = { format: '%v %c', code: 'USD', maxFraction: 6, minFraction: 4 };
  let format = formatCurrency(value, opts);
  format = _.replace(format, ',', '');
  if (prefix) {
    return parseFloat(format).toFixed(4) + ' USD';
  }
  return parseFloat(format).toFixed(4);
}

/**
 * Format number with commas
 * @param {number} num
 * @returns {string}
 */
export function formatNumber(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Format percentage
 * @param {number} value
 * @param {number} decimals
 * @returns {string}
 */
export function formatPercent(value, decimals = 2) {
  return `${parseFloat(value).toFixed(decimals)}%`;
}
